import { configureCustomerRegistryTransport, notifyCustomerRegistryWrite } from './utils/customerRegistryEvents';
import axios from 'axios';

const googleTranslateSuffix = '.translate.goog';
const googleTranslateHost = 'translate.googleusercontent.com';

const decodeGoogleTranslateHost = (encodedHost = '') => {
  if (!encodedHost) return '';
  let decoded = '';
  for (let index = 0; index < encodedHost.length; index += 1) {
    const character = encodedHost[index];
    if (character === '-') {
      if (encodedHost[index + 1] === '-') {
        decoded += '-';
        index += 1;
      } else {
        decoded += '.';
      }
    } else {
      decoded += character;
    }
  }
  return decoded;
};

const resolveTranslatedOrigin = (location) => {
  if (!location) return null;
  const { hostname, protocol, search } = location;

  if (hostname.endsWith(googleTranslateSuffix)) {
    const encodedHost = hostname.slice(0, -googleTranslateSuffix.length);
    const decodedHost = decodeGoogleTranslateHost(encodedHost);
    if (!decodedHost) return null;

    const params = new URLSearchParams(search);
    const scheme = params.get('_x_tr_sch');
    const translatedPort = params.get('_x_tr_port');
    const resolvedProtocol = scheme ? `${scheme}:` : protocol;
    const portSegment = translatedPort ? `:${translatedPort}` : '';
    return `${resolvedProtocol}//${decodedHost}${portSegment}`;
  }

  if (hostname === googleTranslateHost) {
    const params = new URLSearchParams(search);
    const originalUrl = params.get('u') || params.get('url');
    if (originalUrl) {
      try {
        return new URL(originalUrl).origin;
      } catch {
        return null;
      }
    }
  }
  return null;
};

const resolveBaseURL = () => {
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;

  if (typeof window !== 'undefined') {
    const runtimeGlobal =
      window.__APP_API_URL__ ||
      window.__API_BASE_URL__ ||
      window.APP_API_BASE_URL;
    if (runtimeGlobal) return runtimeGlobal;

    const runtimeMeta =
      typeof document !== 'undefined'
        ? document.querySelector('meta[name="api-base-url"]')?.content
        : null;
    if (runtimeMeta) return runtimeMeta;

    const translatedOrigin = resolveTranslatedOrigin(window.location);
    if (translatedOrigin) return translatedOrigin;

    const { protocol, hostname, port } = window.location;
    const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
    const effectivePort = port || (isLocalhost ? '3001' : '');
    const portSegment = effectivePort ? `:${effectivePort}` : '';
    return `${protocol}//${hostname}${portSegment}`;
  }

  return 'http://localhost:3001';
};

const api = axios.create({
  baseURL: resolveBaseURL(),
  withCredentials: true,
});

const extractTokenFromHeaderValue = (value) => {
  if (typeof value !== 'string') return '';
  const trimmed = value.trim();
  if (!trimmed) return '';
  if (/^Bearer\s+/i.test(trimmed)) {
    return trimmed.replace(/^Bearer\s+/i, '').trim();
  }
  return trimmed;
};

const resolveTokenFromHeaders = (headers) => {
  if (!headers) return '';
  try {
    if (typeof headers.get === 'function') {
      return extractTokenFromHeaderValue(
        headers.get('Authorization') || headers.get('authorization')
      );
    }
  } catch {
    // Usa a leitura direta como fallback.
  }
  return extractTokenFromHeaderValue(
    headers.Authorization || headers.authorization
  );
};

const getDefaultHeadersToken = () => {
  const defaults = api?.defaults?.headers;
  if (!defaults) return '';
  const common = typeof defaults.get === 'function' ? defaults : defaults.common;
  return common ? resolveTokenFromHeaders(common) : '';
};

const getEffectiveAuthToken = (config) => {
  const configToken = resolveTokenFromHeaders(config?.headers);
  if (configToken) return configToken;
  return getDefaultHeadersToken();
};

const ensureHeadersWithToken = (config, token) => {
  if (!config) return;
  const nextConfig = config;
  if (!nextConfig.headers) nextConfig.headers = {};
  const headers = nextConfig.headers;

  const hasHeader = (name) => {
    if (typeof headers.has === 'function') {
      return headers.has(name) || headers.has(name.toLowerCase());
    }
    return (
      Object.prototype.hasOwnProperty.call(headers, name) ||
      Object.prototype.hasOwnProperty.call(headers, name.toLowerCase())
    );
  };

  if (!hasHeader('Authorization')) {
    if (typeof headers.set === 'function') {
      headers.set('Authorization', `Bearer ${token}`);
    } else {
      headers.Authorization = `Bearer ${token}`;
    }
  }
};

api.interceptors.request.use((config) => {
  if (typeof window === 'undefined' || config?.skipAuthHeader) return config;
  const effectiveToken = getEffectiveAuthToken(config);
  if (!effectiveToken) return config;
  const nextConfig = { ...config };
  ensureHeadersWithToken(nextConfig, effectiveToken);
  return nextConfig;
});

const TRANSIENT_RETRY_STATUS = new Set([502, 503, 504]);
const TRANSIENT_RETRY_LIMIT = 1;

const transientRetryDelay = (attempt) =>
  Math.min(900 * Math.max(Number(attempt) || 1, 1), 3000);

const wait = (milliseconds) =>
  new Promise((resolve) => setTimeout(resolve, milliseconds));

const shouldRetryTransientRequest = (error) => {
  const config = error?.config;
  if (!config || config.skipTransientRetry || config.signal?.aborted) return false;
  if (axios.isCancel(error)) return false;

  const method = String(config.method || 'get').toLowerCase();
  if (!['get', 'head'].includes(method)) return false;

  const status = Number(error?.response?.status || 0);
  // 504 significa que a requisicao anterior ja ocupou o servidor ate o limite do gateway.
  // Repeti-la imediatamente cria copias da mesma consulta e piora a sobrecarga.
  if (status === 504) return false;
  if (TRANSIENT_RETRY_STATUS.has(status)) return true;

  if (error?.response) return false;
  return ['ERR_NETWORK', 'ECONNABORTED', 'ETIMEDOUT'].includes(
    String(error?.code || '')
  );
};

const makeTransientErrorFriendly = (error) => {
  const status = Number(error?.response?.status || 0);
  if (status === 504) {
    error.message =
      'A solicitação demorou mais do que o servidor permite. Tente novamente; a tela não repetirá a consulta automaticamente.';
  } else if (TRANSIENT_RETRY_STATUS.has(status)) {
    error.message =
      'O servidor está temporariamente indisponível. Tente novamente em alguns segundos.';
  }
  return error;
};

api.interceptors.response.use(
  (response) => {
    notifyCustomerRegistryWrite(response);
    const method = String(response?.config?.method || '').toLowerCase();
    const requestUrl = String(response?.config?.url || '');
    const data = response?.data;
    const isEditableExternalUpload =
      method === 'post' &&
      /\/upload\/external\/(?:forms|step-tasks)\//i.test(requestUrl);

    if (
      isEditableExternalUpload &&
      data &&
      typeof data === 'object' &&
      typeof data.managementToken === 'string' &&
      data.managementToken.trim() &&
      data.metadata &&
      typeof data.metadata === 'object'
    ) {
      // ExternalForm já copia `metadata` para o estado do formulário. A
      // propriedade não enumerável permite que FileAttachment use a credencial
      // nesta aba sem enviá-la no JSON final ou persistir o segredo no processo.
      Object.defineProperty(data.metadata, '__managementToken', {
        value: data.managementToken.trim(),
        enumerable: false,
        configurable: true,
      });
    }

    return response;
  },
  async (error) => {
    const config = error?.config;
    if (!shouldRetryTransientRequest(error)) {
      return Promise.reject(makeTransientErrorFriendly(error));
    }

    const retries = Number(config.__transientRetryCount || 0);
    if (retries >= TRANSIENT_RETRY_LIMIT) {
      return Promise.reject(makeTransientErrorFriendly(error));
    }

    config.__transientRetryCount = retries + 1;
    await wait(transientRetryDelay(config.__transientRetryCount));
    return api.request(config);
  },
);

configureCustomerRegistryTransport(signal => {
  const token = getDefaultHeadersToken();
  return fetch(api.getUri({ url: '/customers/changes' }), {
    signal, credentials: 'include', cache: 'no-store',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
});

export default api;
